import { useTranslation } from 'react-i18next';
import { Button, Skeleton, SkeletonText } from '@/components/ui';
import { AppCoverageRail } from '../RoleCoverage/AppCoverageRail';
import { RoleCoverageRail } from '../RoleCoverage/RoleCoverageRail';
import { RoleAxisLinksCard } from '../RoleCoverage/RoleAxisLinksCard';
import { RoleEditorCard } from '../RoleCoverage/RoleEditorCard';
import { useRoleAxisBoard } from '../RoleCoverage/useRoleAxisBoard';
import type { MatrixCell } from '../RoleCoverage/types';
import type { RoleCoverageVM } from '../RoleCoverage/useRoleCoverage';
import railStyles from '../RoleCoverage/CoverageRail.module.css';
import styles from './RoleMapBoardView.module.css';

/** Which side of the app × role pair the rail lists. */
export type BoardAxis = 'app' | 'role';

/**
 * EAA-1841/EAA-1817: the **board** views of "Liên kết vai trò" — a 300px rail
 * plus a detail pane, in the two orientations frame 55's switcher offers.
 *
 * `axis="app"` is frame 55 itself: a rail of applications and the selected
 * app's standard-role table, with the chip editor inline. `axis="role"` is the
 * same shell read the other way — a rail of standard roles and the selected
 * role's per-application table (`RoleAxisLinksCard`, which explains why it
 * reads rather than edits).
 *
 * ONE COMPONENT, TWO AXES, rather than two sibling views: the shell (loading
 * skeleton, error retry, the two-column grid) is identical, and the axis only
 * decides which rail and which pane go into it. Splitting it would leave two
 * copies of the loading and error states to keep in step, which is how the
 * rails themselves drifted before `CoverageRailShell`.
 *
 * BOTH ARE ECOSYSTEM-DEFAULT ONLY and that is a backend fact, not a choice:
 * `fetchRoleMapCoverage` filters `organization__isnull=True` and takes no
 * organization param at all. The list view's "Phạm vi" control therefore has
 * nothing to switch here — see `RoleMapPage` for the notice that says so rather
 * than showing a scope control that would silently do nothing.
 */
export function RoleMapBoardView({
    vm,
    axis = 'app',
    onOpenCell,
}: {
    vm: RoleCoverageVM;
    axis?: BoardAxis;
    onOpenCell?: (cell: MatrixCell) => void;
}) {
    const { t } = useTranslation();

    // Hooks before the guards below — this component returns early on
    // loading/error, and the role axis needs its state either way.
    const roleAxis = useRoleAxisBoard(vm.matrix);

    if (vm.isLoading) {
        return (
            <div className={styles.columns} role="status" aria-live="polite">
                <span className="portal-sr-only">
                    {t('console.roleMap.boardView.srLoadingText')}
                </span>
                <div className={railStyles.rail}>
                    <SkeletonText lines={2} />
                    <Skeleton height={34} />
                    <Skeleton height={220} />
                </div>
                <div className={styles.rightCol}>
                    <div className="hz-card">
                        <SkeletonText lines={2} />
                        <Skeleton height={160} />
                    </div>
                </div>
            </div>
        );
    }

    if (vm.isError) {
        return (
            <p className="portal-admin-error">
                {t('console.roleMap.boardView.errorText')}{' '}
                <Button onClick={vm.retry}>{t('console.roleMap.boardView.retry')}</Button>
            </p>
        );
    }

    if (axis === 'role') {
        return (
            <div className={styles.columns}>
                <RoleCoverageRail vm={roleAxis} />
                <div className={styles.rightCol}>
                    <RoleAxisLinksCard
                        vm={roleAxis}
                        columns={vm.matrix.allColumns}
                        onOpenCell={vm.canManage ? onOpenCell : undefined}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.columns}>
            <AppCoverageRail vm={vm.board} />
            {/* EAA-1817: `CoverageMatrixCard` used to sit above the editor here.
                It is gone, not moved: design frame 55 has no matrix on this view
                at all — the matrix is its own view now (frame 55a,
                `RoleMapMatrixView`). The card was a leftover of a design
                revision the handoff's own changelog reverted on 2026-08-11 ("Bỏ
                ma trận, xem độ phủ bằng bảng chuẩn"), and it drew the grid
                TRANSPOSED (apps down, roles across) from the one the design came
                back with, so it could not simply be lifted across. */}
            <div className={styles.rightCol}>
                <RoleEditorCard vm={vm} />
            </div>
        </div>
    );
}

export default RoleMapBoardView;
